import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { senderEmailService, regionService, branchService } from '../services/api';
import { Card, Input, Button, Select, useToast } from './common';
import Breadcrumbs from './common/Breadcrumbs';
import { usePageTitle } from '../context/PageTitleContext';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CreateEditSenderEmail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const toast = useToast();
  const { setPageTitle } = usePageTitle();

  const isEditMode = !!id && location.pathname.includes('/edit/');
  const isViewMode = !!id && location.pathname.includes('/view/');

  const [email, setEmail] = useState('');
  const [regionCode, setRegionCode] = useState('');
  const [branchCode, setBranchCode] = useState('');

  const [regions, setRegions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [error, setError] = useState('');

  const [emailError, setEmailError] = useState('');
  const [regionError, setRegionError] = useState('');
  const [branchError, setBranchError] = useState('');

  // Tracks whether the branch list has been loaded for the current region at least once.
  // Used to avoid clearing the preloaded branch_code (coming from getById) on first load.
  const [branchesInitialized, setBranchesInitialized] = useState(false);

  useEffect(() => {
    setPageTitle(isViewMode ? 'View Sender Address' : isEditMode ? 'Edit Sender Address' : 'Add Sender Address');
  }, [isEditMode, isViewMode, setPageTitle]);

  // Load regions once
  useEffect(() => {
    const loadRegions = async () => {
      try {
        const response = await regionService.getAll({ noPagination: true });
        const rows = response.data.data || response.data || [];
        setRegions(rows.map(r => ({ value: r.code, label: r.name })));
      } catch (err) {
        console.error('Failed to load regions', err);
        toast.error('Failed to load regions');
      }
    };
    loadRegions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load record when editing/viewing
  useEffect(() => {
    if (!id || (!isEditMode && !isViewMode)) return;
    const loadRecord = async () => {
      try {
        setLoadingInitial(true);
        const response = await senderEmailService.getById(id);
        const data = response.data || {};
        setEmail(data.email || '');
        setRegionCode(data.region_code || '');
        setBranchCode(data.branch_code || '');
      } catch (err) {
        console.error(err);
        toast.error('Failed to fetch sender address details');
        navigate('/admin/sender-emails');
      } finally {
        setLoadingInitial(false);
      }
    };
    loadRecord();
  }, [id, isEditMode, isViewMode, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load branches whenever the selected region changes
  useEffect(() => {
    const loadBranches = async () => {
      if (!regionCode) {
        setBranches([]);
        setBranchesInitialized(true);
        return;
      }
      try {
        setLoadingBranches(true);
        const response = await branchService.getAll({ noPagination: true, region_codes: regionCode });
        const rows = response.data.data || [];
        const options = rows.map(b => ({ value: b.code, label: b.name }));
        setBranches(options);

        // If the currently selected branch doesn't belong to this region, clear it —
        // but only after the first load has completed (so we don't wipe a preloaded
        // branchCode that's about to arrive in the options).
        if (branchesInitialized && branchCode && !options.some(o => o.value === branchCode)) {
          setBranchCode('');
        }
        setBranchesInitialized(true);
      } catch (err) {
        console.error('Failed to load branches', err);
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };
    loadBranches();
  }, [regionCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRegionChange = (e) => {
    const newRegion = e.target.value;
    setRegionCode(newRegion);
    // Clear branch immediately so the user must pick one from the new region.
    setBranchCode('');
    if (regionError) setRegionError('');
    if (branchError) setBranchError('');
  };

  const handleBranchChange = (e) => {
    setBranchCode(e.target.value);
    if (branchError) setBranchError('');
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (emailError) setEmailError('');
  };

  const validate = () => {
    let ok = true;
    if (!email.trim()) {
      setEmailError('Email is required');
      ok = false;
    } else if (!EMAIL_REGEX.test(email.trim())) {
      setEmailError('Invalid email format');
      ok = false;
    }
    if (!regionCode) {
      setRegionError('Region is required');
      ok = false;
    }
    if (!branchCode) {
      setBranchError('Branch is required');
      ok = false;
    }
    return ok;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');
    setRegionError('');
    setBranchError('');
    setError('');

    if (!validate()) return;

    const normalizedEmail = email.trim().toLowerCase();

    try {
      setSubmitting(true);

      const payload = { email: normalizedEmail, region_code: regionCode, branch_code: branchCode };
console.log('Submitting sender email with payload:', payload);
      if (isEditMode) {
        await senderEmailService.update(id, payload);
        toast.success('Sender address updated successfully');
      } else {
        await senderEmailService.create(payload);
        toast.success('Sender address created successfully');
      }

      navigate('/admin/sender-emails');
    } catch (err) {
      const msg = err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} sender address`;
      setError(msg);
      toast.error(msg);
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => navigate('/admin/sender-emails');

  if (loadingInitial) {
    return (
      <div className="admin-panel">
        <Card>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Loading sender address details...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <Card className="w-full">
        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded border-l-4 border-danger-600">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} noValidate className="w-full">
          <Breadcrumbs
            items={[
              { label: 'Sender Addresses', to: '/admin/sender-emails' },
              { label: isViewMode ? 'View Sender Address' : isEditMode ? 'Edit Sender Address' : 'Add Sender Address' }
            ]}
            className="mb-6"
          />

          <div className="flex flex-col md:flex-row gap-4 mb-6 w-full">
            <div className="flex-1">
              <Input
                label="Email"
                name="email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="Enter email address"
                required
                error={emailError}
                disabled={submitting || isViewMode}
                readOnly={isViewMode}
              />
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4 mb-8 w-full">
            <div className="flex-1">
              <Select
                label="Region"
                name="region_code"
                value={regionCode}
                onChange={handleRegionChange}
                options={regions}
                placeholder="Select region"
                required
                error={regionError}
                disabled={submitting || isViewMode}
              />
            </div>
            <div className="flex-1">
              <Select
                label="Branch"
                name="branch_code"
                value={branchCode}
                onChange={handleBranchChange}
                options={branches}
                placeholder={!regionCode ? 'Select region first' : loadingBranches ? 'Loading branches...' : 'Select branch'}
                required
                error={branchError}
                disabled={submitting || isViewMode || !regionCode || loadingBranches}
              />
            </div>
          </div>

          <div className="flex w-full justify-end gap-3 mt-8 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={submitting}
              className="border-primary-600 text-primary-600 hover:bg-primary-50"
            >
              {isViewMode ? 'Back' : 'Cancel'}
            </Button>
            {!isViewMode && (
              <Button
                type="submit"
                variant="primary"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : isEditMode ? 'Update Sender Address' : '+ Add Sender Address'}
              </Button>
            )}
            {isViewMode && (
              <Button
                type="button"
                variant="primary"
                onClick={() => navigate(`/admin/sender-emails/edit/${id}`)}
              >
                Edit Sender Address
              </Button>
            )}
          </div>
        </form>
      </Card>
    </div>
  );
};

export default CreateEditSenderEmail;
